import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { Receipt } from '../pos/api'
import type { ChangeMethodInput, PaymentMethodChangeItem } from '../payments/api'
import { financeKeys } from '../finance/api'

/**
 * Documents, messaging and eTIMS (milestone 16): the sales register, the message log,
 * the templates and the suppression list.
 */

export type Channel = 'Sms' | 'WhatsApp' | 'Email'
export type MessageStatus = 'Queued' | 'Sent' | 'Delivered' | 'Failed'
export type MessageTrigger =
  | 'SaleCompleted' | 'PaymentReceived' | 'Statement' | 'CreditReminder' | 'WarrantyExpiring' | 'LoyaltyTierUpgraded'
  | 'DeliveryDispatched' | 'DeliveryCompleted' | 'DeliveryFailed' | 'Promotion'
export type MessageCategory = 'Transactional' | 'Marketing'
export type EtimsStatus = 'Pending' | 'Transmitted' | 'Failed' | 'AwaitingClassification'

/** What a sale rung without VAT is reported as. Chosen after the sale, not at the till (D-224). */
export type VatWaiverBasis = 'Exempt' | 'ZeroRated' | 'Export'

export const channelLabels: Record<Channel, string> = { Sms: 'SMS', WhatsApp: 'WhatsApp', Email: 'Email' }
export const triggerLabels: Record<MessageTrigger, string> = {
  SaleCompleted: 'Sale completed', PaymentReceived: 'Payment received', Statement: 'Statement', CreditReminder: 'Credit reminder',
  WarrantyExpiring: 'Warranty expiring', LoyaltyTierUpgraded: 'Loyalty tier upgraded', DeliveryDispatched: 'Delivery dispatched',
  DeliveryCompleted: 'Delivery completed', DeliveryFailed: 'Delivery failed', Promotion: 'Campaign',
}

export const messagingKeys = {
  all: ['messaging'] as const,
  invoices: (filters: object) => ['messaging', 'invoices', filters] as const,
  invoice: (id: string) => ['messaging', 'invoice', id] as const,
  messages: (filters: object) => ['messaging', 'messages', filters] as const,
  status: ['messaging', 'status'] as const,
  templates: ['messaging', 'templates'] as const,
  suppressions: ['messaging', 'suppressions'] as const,
  inbound: ['messaging', 'inbound'] as const,
}

// --- the register -----------------------------------------------------------------------

export type InvoiceListItem = {
  id: string; code: string; orderNumber?: string; invoiceDate: string; issuedAt: string; locationId: string; locationName: string
  customerId: string; customerName: string; cashierName?: string; saleType: 'Cash' | 'Credit'; grandTotal: number; paidBy: string
  etimsStatus?: EtimsStatus; etimsInvoiceNumber?: string; isReversed: boolean
}

export type InvoicePage = { items: InvoiceListItem[]; total: number; page: number; pageSize: number; totalValue: number }

export type EtimsDetail = {
  status: EtimsStatus; invoiceNumber?: string; controlUnitId?: string; receiptSignature?: string; qrPayload?: string
  transmittedAt?: string; attempts: number; lastError?: string; nextAttemptAt?: string; isSandbox: boolean
}

export type MessageItem = {
  id: string; channel: Channel; category: MessageCategory; trigger: MessageTrigger; recipient: string; subject?: string; body: string
  status: MessageStatus; failureReason?: string; provider?: string; isSandbox: boolean; segments: number; cost?: number
  costIsEstimate: boolean; attempts: number; queuedAt: string; sentAt?: string; deliveredAt?: string; failedAt?: string
  documentType?: string; documentId?: string; documentCode?: string; customerId?: string; customerName?: string
  attachment: 'None' | 'Receipt' | 'TaxInvoice' | 'Statement'
}

/** One tender on a sale, named so that its method can be corrected (D-274). */
export type SaleTenderItem = {
  id: string; mode: string; amount: number; reference?: string; sequence: number
  mpesaPaymentId?: string; canChangeMethod: boolean
}

export type InvoiceDetail = { receipt: Receipt; etims?: EtimsDetail; messages: MessageItem[]; isReversed: boolean
  /** Null while nobody has said what the missing VAT was (D-224). */
  vatWaiverBasis?: VatWaiverBasis | null
  tenders?: SaleTenderItem[]
  methodChanges?: PaymentMethodChangeItem[] }

export type InvoiceFilters = { from?: string; to?: string; locationId?: string; search?: string; etims?: EtimsStatus; page: number }

export function useInvoices(filters: InvoiceFilters) {
  return useQuery({
    queryKey: messagingKeys.invoices(filters),
    queryFn: () => api<InvoicePage>(`/api/sales/invoices?${query(filters)}`),
    placeholderData: keepPreviousData,
  })
}

/** Polls while anything about the invoice is still on its way — a message, or the eTIMS report. */
export function useInvoice(id: string | undefined) {
  return useQuery({
    queryKey: messagingKeys.invoice(id ?? ''),
    queryFn: () => api<InvoiceDetail>(`/api/sales/invoices/${id}`),
    enabled: !!id,
    refetchInterval: (q) => {
      const d = q.state.data
      if (!d) return false
      const moving = d.messages.some((m) => m.status === 'Queued' || m.status === 'Sent') || d.etims?.status === 'Pending'
      return moving ? 2000 : false
    },
  })
}

/** The figures above the register (D-154), over everything the filter matches. */
export type PaymentSlice = { mode: string; count: number; amount: number }
export type RegisterSummary = {
  total: number; count: number; collected: number; credit: number; changePercent?: number
  etimsSubmitted: number; etimsTotal: number; change: number; expectedInDrawer?: number
  byPayment: PaymentSlice[]
  byDay: { date: string; total: number }[]
}

export function useRegisterSummary(filters: InvoiceFilters) {
  const query = new URLSearchParams()
  if (filters.from) query.set('from', filters.from)
  if (filters.to) query.set('to', filters.to)
  if (filters.locationId) query.set('locationId', filters.locationId)
  if (filters.search) query.set('search', filters.search)
  if (filters.etims) query.set('etims', filters.etims)

  return useQuery({
    queryKey: ['messaging', 'register-summary', query.toString()],
    queryFn: ({ signal }) => api<RegisterSummary>(`/api/sales/invoices/summary?${query}`, { signal }),
    placeholderData: keepPreviousData,
    staleTime: 0,
  })
}

export function useSendReceipt(invoiceId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { channel: Channel; recipient?: string }) =>
      api<{ messageId: string; channel: Channel; recipient: string; status: MessageStatus }>(`/api/sales/invoices/${invoiceId}/send`, { method: 'POST', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: messagingKeys.all }),
  })
}

/** The receipt written out for WhatsApp on this device (D-151), with the wa.me address to open. */
export type WhatsAppHandover = { phone: string; text: string; url: string }

export function useWhatsAppHandover(invoiceId: string, recipient: string, enabled: boolean) {
  return useQuery({
    queryKey: ['messaging', 'whatsapp', invoiceId, recipient],
    queryFn: ({ signal }) =>
      api<WhatsAppHandover>(`/api/sales/invoices/${invoiceId}/whatsapp?recipient=${encodeURIComponent(recipient)}`, { signal }),
    enabled: enabled && recipient.trim().length >= 9,
    staleTime: 60_000,
    retry: false,
  })
}

/** Records that it was handed to WhatsApp, so the Message Center shows it beside the sent ones. */
export function useRecordWhatsAppHandover(invoiceId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (recipient: string) =>
      api<{ messageId: string; channel: Channel; recipient: string; status: MessageStatus }>(
        `/api/sales/invoices/${invoiceId}/whatsapp/handed-over`, { method: 'POST', body: { channel: 'WhatsApp', recipient } }),
    onSuccess: () => client.invalidateQueries({ queryKey: messagingKeys.all }),
  })
}

export function useRetryEtims(invoiceId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api<void>(`/api/sales/invoices/${invoiceId}/etims/retry`, { method: 'POST' }),
    onSuccess: () => client.invalidateQueries({ queryKey: messagingKeys.all }),
  })
}

/**
 * Corrects how one tender on a sale was paid (D-274).
 *
 * Invalidates the finance keys as well as the sale's own: it posts a contra entry and may take a
 * payment out of the M-Pesa inbox, so the ledger and the inbox screens are both out of date.
 */
export function useChangeTenderMethod(invoiceId: string, tenderId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: ChangeMethodInput) =>
      api<InvoiceDetail>(`/api/sales/invoices/${invoiceId}/tenders/${tenderId}/method`, { method: 'POST', body: input }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: messagingKeys.all })
      client.invalidateQueries({ queryKey: financeKeys.all })
    },
  })
}

/** Says what a waived sale was, which also releases its held eTIMS submission (D-224). */
export function useClassifyWaiver(invoiceId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (basis: VatWaiverBasis) =>
      api<void>(`/api/sales/invoices/${invoiceId}/vat-waiver/classify`, { method: 'POST', body: { basis } }),
    onSuccess: () => client.invalidateQueries({ queryKey: messagingKeys.all }),
  })
}

// --- the message log --------------------------------------------------------------------

export type MessageFilters = { channel?: Channel; status?: MessageStatus; trigger?: MessageTrigger; search?: string; from?: string; to?: string; campaignId?: string; page: number; pageSize?: number }

export type MessagePage = {
  items: MessageItem[]; total: number; page: number; pageSize: number
  totals: { queued: number; sent: number; delivered: number; failed: number; cost: number; costIncludesEstimates: boolean }
}

export type ChannelStatus = { channel: Channel; provider: string; isSandbox: boolean; unitCost: number }

export type MessagingStatus = {
  channels: ChannelStatus[]; etimsProvider: string; etimsIsSandbox: boolean
  queued: number; failedToday: number; etimsPending: number; etimsFailed: number
}

export function useMessages(filters: MessageFilters) {
  return useQuery({
    queryKey: messagingKeys.messages(filters),
    queryFn: () => api<MessagePage>(`/api/messages?${query(filters)}`),
    placeholderData: keepPreviousData,
    refetchInterval: 5000,
  })
}

export function useMessagingStatus(enabled = true) {
  return useQuery({ queryKey: messagingKeys.status, queryFn: () => api<MessagingStatus>('/api/messages/status'), enabled })
}

export function useRetryMessage() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/messages/${id}/retry`, { method: 'POST' }),
    onSuccess: () => client.invalidateQueries({ queryKey: messagingKeys.all }),
  })
}

// --- templates ------------------------------------------------------------------------------

export type TemplatePreview = { subject?: string; body: string; segments: number; unicode: boolean; unicodeCharacter?: string; estimatedCost: number }

export type TemplateItem = {
  id: string; trigger: MessageTrigger; channel: Channel; category: MessageCategory; name: string; subject?: string; body: string
  isActive: boolean; providerTemplateName?: string; providerLanguage?: string; fields: string[]; preview: TemplatePreview
}

export function useTemplates() {
  return useQuery({ queryKey: messagingKeys.templates, queryFn: () => api<TemplateItem[]>('/api/messages/templates') })
}

export function useEditTemplate(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { subject?: string; body: string; providerTemplateName?: string; providerLanguage?: string }) =>
      api<TemplateItem>(`/api/messages/templates/${id}`, { method: 'PUT', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: messagingKeys.templates }),
  })
}

export function useSetTemplateActive() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => api<TemplateItem>(`/api/messages/templates/${id}/active`, { method: 'PUT', body: { active } }),
    onSuccess: () => client.invalidateQueries({ queryKey: messagingKeys.templates }),
  })
}

export function usePreviewTemplate() {
  return useMutation({
    mutationFn: (body: { trigger: MessageTrigger; channel: Channel; subject?: string; body: string }) =>
      api<TemplatePreview>('/api/messages/templates/preview', { method: 'POST', body }),
  })
}

// --- suppression and replies ----------------------------------------------------------------

export type SuppressionItem = { id: string; channel: Channel; address: string; reason: string; suppressedAt: string; isActive: boolean; liftedAt?: string }
export type InboundItem = { id: string; channel: Channel; from: string; text: string; receivedAt: string; outcome: string }

export function useSuppressions() {
  return useQuery({ queryKey: messagingKeys.suppressions, queryFn: () => api<SuppressionItem[]>('/api/messages/suppressions') })
}

export function useSuppress() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { channel: Channel; address: string; reason: string }) => api<SuppressionItem>('/api/messages/suppressions', { method: 'POST', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: messagingKeys.suppressions }),
  })
}

export function useLiftSuppression() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/messages/suppressions/${id}/lift`, { method: 'POST' }),
    onSuccess: () => client.invalidateQueries({ queryKey: messagingKeys.suppressions }),
  })
}

export function useInbound() {
  return useQuery({ queryKey: messagingKeys.inbound, queryFn: () => api<InboundItem[]>('/api/messages/inbound') })
}

// --- statements -------------------------------------------------------------------------------

export function useSendStatement(customerId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { channel: Channel; recipient?: string }) =>
      api<{ messageId: string; recipient: string; status: MessageStatus }>(`/api/debtors/${customerId}/send-statement`, { method: 'POST', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: messagingKeys.all }),
  })
}

// --- documents --------------------------------------------------------------------------------

export type DocumentKind = 'receipt' | 'tax-invoice' | 'delivery-note' | 'picking-note' | 'statement' | 'credit-note' | 'customer-delivery' | 'purchase-order' | 'proforma'
  /** D-247: what goes on the box, and the codes-and-counts list that travels inside it. */
  | 'dispatch-labels' | 'dispatch-manifest'

/**
 * Opens a document in a new tab (D-097). The tab is opened at once — a browser blocks
 * one opened after an await — and pointed at a two-minute signed link the API issues
 * after its usual checks. Not a blob: a blob document inherits the app's security policy,
 * which stops Chrome's PDF viewer, and iOS Safari will not reliably open one in a tab.
 */
export async function openPdf(document: { kind: DocumentKind; id: string; from?: string; to?: string }): Promise<void> {
  const tab = window.open('', '_blank')
  try {
    const link = await api<{ url: string }>('/api/documents/links', { method: 'POST', body: document })
    if (tab) tab.location.href = link.url
    else window.location.assign(link.url)
  } catch (error) {
    tab?.close()
    throw error
  }
}

/** The same document, saved as a file (named for it) rather than shown in a tab. */
export async function downloadPdf(document: { kind: DocumentKind; id: string; from?: string; to?: string }): Promise<void> {
  const link = await api<{ url: string }>('/api/documents/links', { method: 'POST', body: document })
  // An attachment does not replace the page: the browser saves it and stays here.
  window.location.assign(`${link.url}${link.url.includes('?') ? '&' : '?'}download=true`)
}

function query(filters: object): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  }
  return params.toString()
}
